"use client";
import {
  GIFS,
  LOADER_QUOTES,
  OFFER_QUOTES,
  STAGE,
  WEB_COLORS,
} from "@/lib/constants";
import {
  Box,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Image from "next/image";
import { useEffect, useState } from "react";
import PaperComponent from "./Paper";
import ButtonComponent from "./Button";

const TOTAL_TIME = 3 * 60 * 1000;
const INTERVAL = 500;
export default function LoaderComponent({
  open,
  loadingLabel = "Please wait while page is loading...",
  stage = "",
  onRetry,
  butttonLabel = "Retry",
  retryReqd = false,
}) {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);

  useEffect(() => {
    let timer;

    setProgress(0);
    setTimeLeft(TOTAL_TIME);

    if (stage !== STAGE.MANDATE) {
      timer = setInterval(() => {
        setProgress((p) => Math.min(p + 5, 95));
      }, 500);
    } else {
      const startTime = Date.now();
      timer = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const clampedElapsed = Math.min(elapsed, TOTAL_TIME);

        setProgress((clampedElapsed / TOTAL_TIME) * 100);
        setTimeLeft(TOTAL_TIME - clampedElapsed);

        if (clampedElapsed >= TOTAL_TIME) clearInterval(timer);
      }, INTERVAL);
    }

    return () => clearInterval(timer);
  }, [stage]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(
        (prevIndex) =>
          (prevIndex + 1) %
          (stage === STAGE.OFFERS
            ? OFFER_QUOTES?.length
            : LOADER_QUOTES?.length)
      );
    }, 3000);
    return () => clearInterval(interval);
  }, [LOADER_QUOTES?.length, currentIndex, OFFER_QUOTES?.length, stage]);

  const formatTime = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };
  const renderLoader = () => {
    return stage !== STAGE.MANDATE ? (
      <>
        <Stack justifyContent="center" alignItems="center" spacing={3} px={2}>
          <Image src={GIFS.loader} height={200} width={200} alt="loader" />
          <Typography
            align="center"
            variant="h6"
            color={WEB_COLORS.darkTeal}
            gutterBottom
          >
            {loadingLabel ? loadingLabel : LOADER_QUOTES[currentIndex]?.label}
          </Typography>

          <Box sx={{ minWidth: 300, width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Stack mt={5}>
            <Typography
              align="center"
              color={WEB_COLORS.darkTeal}
              gutterBottom
              fontWeight={700}
            >
              {stage === STAGE.OFFERS
                ? OFFER_QUOTES[currentIndex]?.label
                : LOADER_QUOTES[currentIndex]?.label}
            </Typography>
            {stage === STAGE.OFFERS ? (
              <Typography
                align="center"
                color={WEB_COLORS.darkTeal}
                gutterBottom
                fontWeight={400}
              >
                {OFFER_QUOTES[currentIndex]?.subLabel}
              </Typography>
            ) : null}
          </Stack>
        </Stack>

        {retryReqd && (
          <Stack my={2} spacing={2} alignItems="center" justifyContent="center">
            <Typography color={WEB_COLORS.darkTeal} fontWeight={500}>
              Failed to Initiate?
            </Typography>
            <ButtonComponent onClick={onRetry}>{butttonLabel}</ButtonComponent>
          </Stack>
        )}
      </>
    ) : (
      <Stack justifyContent="center" alignItems="center" spacing={3} px={2}>
        <Box sx={{ minWidth: 300, width: "100%" }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography
            mt={1}
            align="center"
            color={WEB_COLORS.darkTeal}
            fontWeight={500}
          >
            Time remaining: {formatTime(timeLeft)}
          </Typography>
        </Box>
      </Stack>
    );
  };
  return (
    <div>
      {stage === STAGE.MANDATE && open ? (
        renderLoader()
      ) : stage === STAGE.OFFERS && open ? (
        <PaperComponent noDrop>
          <Stack
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            {renderLoader()}
          </Stack>
        </PaperComponent>
      ) : (
        <Backdrop
          sx={{
            color: theme.palette.primary.main,
            zIndex: 3000,
          }}
          open={open}
        >
          <PaperComponent noDrop>
            <Stack
              flexDirection={"row"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              {renderLoader()}
            </Stack>
          </PaperComponent>
        </Backdrop>
      )}
    </div>
  );
}
